/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * PropertySearchDataGrid - DataGrid implementation for Property Search screen
 * Migrated from RcmTable to @mdsol/onex-design DataGrid
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import './PropertySearchDataGrid.css'
// @ts-expect-error - createColumnHelper is exported but not in TypeScript definitions
import { DataGrid, createColumnHelper } from '@mdsol/onex-design/DataGrid'

// App hooks
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { useModal } from '~/contexts/ModalContext'
import useDocumentsListService from '../../hooks/useDocumentsListService'
import usePropertySearchService from '../../hooks/usePropertySearchService'
import { useColumnConfiguration } from '../../hooks/useColumnConfigurations'

// App components - reuse existing
import ValidDocIndicator from '../../components/shared/ValidDocIndicator'
import DocIcon from '../../components/shared/DocIcon'
import DocState from '../../components/shared/DocState'
import DueDateIndicator from '../../components/DueDateIndicator'
import TextLinkButton from '../../components/shared/TextLinkButton'
import { WrapperOverlay } from './quality-issues'
import WorkflowDetailsModal from '~/components/modals/WorkflowDetailsModal'
import MultipleWorkflowsModal from '~/components/modals/MultipleWorkflowsModal'
import PropertySearchBulkActionHeader from './PropertySearchBulkActionHeader'
import PropertySearchRowMenu from './PropertySearchRowMenu'

// Constants
import { AclLevel, FsiType } from '../../constants/fsi'
import { DOCUMENTS_VIEW_TYPE } from './types'

// Utils
import { createDateTimeFormatter } from '../../utils/commonColumns'
import persistService from '../../services/persist'
import { PAGE_SIZES_ARRAY } from '~/constants/pagination'
import { buildUrlWithCSelections } from '~/utils/navigation'

// Types
import type { PropertySearchDataGridProps } from './propertySearchDataGridTypes'

// Column helper for TanStack Table
const columnHelper = createColumnHelper<any>()

// Column configuration defaults
const DEFAULT_COLUMN_PINNING = { left: ['name'], right: ['actions'] }
const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {}
const DEFAULT_COLUMN_ORDER: string[] = []

// Helper to extract only the keys that are `true` into a Set
const getActiveKeys = (selectionObj: Record<string, boolean> | undefined): Set<string> => {
  const active = new Set<string>()
  if (!selectionObj) return active
  for (const key in selectionObj) {
    if (selectionObj[key]) active.add(key)
  }
  return active
}

// Helper to perform an O(N) equality check between two Sets
const areSetsEqual = (a: Set<string>, b: Set<string>): boolean => {
  if (a.size !== b.size) return false
  let equal = true
  a.forEach((item) => {
    if (!b.has(item)) equal = false
  })
  return equal
}

/**
 * PropertySearchDataGrid - Main component
 */
export const PropertySearchDataGrid: React.FC<PropertySearchDataGridProps> = ({
  fsi, // this always empty not sure its purspose but its coming from parent compo [path:{}]
  viewType,
  initialPage,
  filterShown,
  hideVersion,
  onNavigateToFolder,
  setWorkFlowModalDetails
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const context = useServiceContext()
  const tableData = useRcmTableData()
  const { assembleFiltersFromFormValues } = usePropertySearchService()
  const { getFsiNameTooltip } = useDocumentsListService()
  const { showModal } = useModal()

  const {
    service: {
      filter: { translate },
      access,
      init
    }
  } = context

  // Feature flags
  const qcReviewEnabled = init.getFeaturesInfo()?.qcReview?.qcReviewEnabled || false
  const isEtmfEnabled = init.getFeaturesInfo()?.etmfFeature?.show || false

  // Access table constants for permission checks
  const accessTable = {
    rcmAdmins: ['ADMIN', 'CONTENT_ADMIN'],
    stagingManagers: ['ETMF_MANAGER', 'ETMF_SPECIALIST'],
    rcmUsers: ['POWER_USER', 'USER'],
    stagingUsers: ['ETMF_SITE_USER', 'ETMF_STUDY_USER']
  }

  // Conditional column visibility states
  const [isSiteNumberShown, setIsSiteNumberShown] = useState(false)
  const [isSubjectShown, setIsSubjectShown] = useState(false)
  const [isDataPageShown, setIsDataPageShown] = useState(false)

  // DataGrid state
  // initialPage is 1-based (from legacy system), but DataGrid pageIndex is 0-based
  const [pagination, setPagination] = useState(() => {
    const persisted = persistService.getGlobal<{ page: number; perPage: number }>('paginate')
    return {
      pageIndex: 0,
      pageSize: persisted?.perPage || 10
    }
  })
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([])

  // Row selection persistence across pages
  // rowSelectionByPages stores selections per page: { 0: { '123': true }, 1: { '456': true } }
  const [rowSelectionByPages, setRowSelectionByPages] = useState<Record<number, Record<string, boolean>>>({})
  const [isAllDataSelected, setIsAllDataSelected] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [isCountLoading, setIsCountLoading] = useState<boolean>(false)

  // --- Column configuration (persisted per user+tenant) ---
  const { columnOrder, columnPinning, columnVisibility, handleColumnOrderChange, handleColumnVisibilityChange } =
    useColumnConfiguration({
      storageKey: 'property-search',
      defaultColumnOrder: DEFAULT_COLUMN_ORDER,
      defaultColumnPinning: DEFAULT_COLUMN_PINNING,
      defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY
    })

  // Guard: track whether data has been loaded at least once (via filter)
  // Prevents calling tableData.load() before user clicks Filter button
  const hasDataLoadedRef = useRef(false)
  const prevSortingRef = useRef(sorting)
  // Track what triggered the last pagination change to avoid duplicate API calls
  const lastPaginationSourceRef = useRef<'user' | 'filter' | 'sort' | 'refresh'>('user')
  // Flag to distinguish DataGrid's forced setRowSelection({}) on pagination/sort change
  // from genuine user uncheck. Set true in onChangeControl, consumed in handleRowSelectionChange.
  const dgInternalClearRef = useRef(false)

  // Determine if sorting should be enabled
  const isSortingEnabled = viewType !== DOCUMENTS_VIEW_TYPE.recent

  // Flattened row selection object from all pages — passed to DataGrid's state.rowSelection
  // resyncTrigger forces a new object reference when DataGrid internally clears selections
  // (on pagination/sort change). Without it, the useMemo returns the same reference,
  // and DataGridTable's sync effect (which depends on [state?.rowSelection]) won't re-fire.
  const [resyncTrigger, setResyncTrigger] = useState(0)
  const allRowSelectionObj = useMemo(() => {
    const merged: Record<string, boolean> = {}
    Object.values(rowSelectionByPages).forEach((pageSelection) => {
      Object.entries(pageSelection).forEach(([key, value]) => {
        if (value) merged[key] = true
      })
    })
    return merged
  }, [rowSelectionByPages, resyncTrigger])

  // Ref to always have the latest allRowSelectionObj for comparison in callbacks
  // without adding it as a dependency (which would cause unnecessary callback ref changes)
  const allRowSelectionRef = useRef(allRowSelectionObj)
  allRowSelectionRef.current = allRowSelectionObj

  // Convert row selection from ALL pages to array of selected FSI IDs
  const selectedIds = useMemo(() => {
    return Object.keys(allRowSelectionObj).map((key) => Number(key))
  }, [allRowSelectionObj])

  // Track selected items' details (id + name) across pages for modals that show document names
  const selectedItemsMapRef = useRef<Map<number, { id: number; name: string }>>(new Map())

  // Derive selectedItems array from the map, filtered to only currently selected IDs
  const selectedItems = useMemo(() => {
    return selectedIds
      .map((id) => selectedItemsMapRef.current.get(id))
      .filter((item): item is { id: number; name: string } => item != null)
  }, [selectedIds])

  // Clear all selections across all pages
  const deselectAll = useCallback(() => {
    setRowSelectionByPages({})
    selectedItemsMapRef.current.clear()
  }, [])

  // Date formatter
  const formatDateTime = createDateTimeFormatter(context.service.auth, translate)

  /**
   * Determines if the quality issue indicator should be shown for a document
   */
  const shouldShowQualityIssueIndicator = useCallback(
    (parentFsi: any, row: any): boolean => {
      return (
        isEtmfEnabled &&
        access.isAllowed([
          accessTable.rcmAdmins,
          accessTable.stagingManagers,
          accessTable.rcmUsers,
          accessTable.stagingUsers
        ] as any) &&
        [AclLevel.BROWSE, AclLevel.VIEW, AclLevel.READ, AclLevel.WRITE, AclLevel.DELETE].includes(parentFsi?.acl) &&
        row?.documentDefective
      )
    },
    [isEtmfEnabled, access, accessTable]
  )

  /**
   * Handle row click navigation
   */
  const handleRowEnter = useCallback(
    (row: any) => {
      if (
        !row.valid ||
        (row.acl === AclLevel.BROWSE && row.type !== FsiType.FOLDER) ||
        row.type === FsiType.PLACEHOLDER
      ) {
        return
      }

      if (row.isReport) {
        window.open(`/api/files/${row.id}/download`, '_blank')
        return
      }

      if (row.type === FsiType.FOLDER) {
        if (row.systemFolderType) {
          const folderId = row.isAbstractFolder ? 0 : row.id
          navigate(`/browser/${folderId}?type=${row.systemFolderType}`)
        } else {
          navigate(`/browser/${row.id}`)
        }
      } else {
        navigate(`/show-file/${row.id}`)
      }
    },
    [navigate]
  )

  /**
   * Per-row selection check passed via `options` prop to TanStack.
   * We pass enableRowSelection={true} (boolean) to DataGrid so the checkbox
   * column always renders, then use this function via options to control
   * which individual rows are selectable.
   */
  const isRowSelectable = useCallback((row: any) => {
    const original = row.original
    const isFolder = original.type === FsiType.FOLDER
    const isInWorkflow = original.workflow?.length > 0
    const isInvalid = !original.valid
    const isLocked = original.fsiLocked

    return !(isFolder || isInWorkflow || isInvalid || isLocked)
  }, [])

  const loadCount = useCallback(async () => {
    if (!tableData.filter) return
    setIsCountLoading(true)
    try {
      const assembledFilter = assembleFiltersFromFormValues({ ...tableData.filter })
      const result = await context.api.files.propertySearchGetCount(assembledFilter)
      if (typeof result === 'object' && 'anyFilterActive' in result && result.anyFilterActive === false) {
        setTotalCount(0)
      } else {
        setTotalCount(typeof result === 'number' ? result : 0)
      }
    } catch (error) {
      console.error('Failed to load count:', error)
      setTotalCount(0)
    } finally {
      setIsCountLoading(false)
    }
  }, [tableData.filter, assembleFiltersFromFormValues, context.api.files])

  // When filter changes: reset to page 0 (rows are loaded by wrapper automatically)
  useEffect(() => {
    if (tableData.filter && Object.keys(tableData.filter).length > 0) {
      setPagination((prev) => {
        if (prev.pageIndex !== 0) {
          // Only set flag when pageIndex actually changes, so the pagination
          // effect fires and consumes it. If pageIndex is already 0, the effect
          // won't fire (deps unchanged) and a stale flag would block the next
          // user-initiated pagination change.
          lastPaginationSourceRef.current = 'filter'
        }
        return { ...prev, pageIndex: 0 }
      })
      hasDataLoadedRef.current = true
      setTotalCount(null)
    }
  }, [tableData.filter])

  // Event bus integration - listen for table update events
  // Debounce ref prevents double-firing when a modal dispatches both
  // 'updateFiles' and 'bctable:updateTable' synchronously.
  const updateScheduledRef = useRef(false)

  useEffect(() => {
    const handleUpdateTable = () => {
      if (updateScheduledRef.current) return
      updateScheduledRef.current = true
      queueMicrotask(() => {
        updateScheduledRef.current = false
      })

      tableData.load(buildLoadParams(pagination.pageIndex, pagination.pageSize))
      setTotalCount(null)
    }

    window.addEventListener('bctable:updateTable', handleUpdateTable)
    window.addEventListener('updateFiles', handleUpdateTable)

    // Reset row selections when the Filter button is clicked
    const handleFilterApplied = () => {
      setRowSelectionByPages({})
      selectedItemsMapRef.current.clear()
    }
    window.addEventListener('propertySearch:filterApplied', handleFilterApplied)

    return () => {
      window.removeEventListener('bctable:updateTable', handleUpdateTable)
      window.removeEventListener('updateFiles', handleUpdateTable)
      window.removeEventListener('propertySearch:filterApplied', handleFilterApplied)
    }
  }, [tableData])

  // Update conditional column visibility based on data
  useEffect(() => {
    // Check if we need to show site number column
    setIsSiteNumberShown(
      fsi?.name === 'Sites' && !['ETMF_SITE_USER', 'ETMF_STUDY_USER'].includes(context.service.auth.getRole() || '')
    )

    // Check supplemental data columns
    const supplementalDataValues = tableData.entries.reduce(
      (memo, file: any) => {
        if (file.supplementalData?.subjectName) {
          memo.isSubjectValuePresent = true
        }
        if (file.supplementalData?.dataPageName) {
          memo.isDataPageValuePresent = true
        }
        return memo
      },
      {
        isSubjectValuePresent: false,
        isDataPageValuePresent: false
      }
    )

    setIsDataPageShown(fsi?.isEDCAppPresent && supplementalDataValues.isDataPageValuePresent)
    setIsSubjectShown(fsi?.isEDCAppPresent && supplementalDataValues.isSubjectValuePresent)
  }, [fsi, tableData.entries, context.service.auth])

  // Mark that data has been loaded when entries arrive (from filter application)
  useEffect(() => {
    if (tableData.entries.length > 0 || tableData.hasNextPage) {
      hasDataLoadedRef.current = true
    }
  }, [tableData.entries, tableData.hasNextPage])

  // Handle sorting changes from DataGrid - just update state, no API call
  const handleSortingChange = useCallback((newSorting: any) => {
    setSorting(newSorting)
  }, [])

  // Helper: build load params from current pagination + sorting state
  const buildLoadParams = useCallback(
    (pageIndex: number, pageSize: number) => ({
      pageStart: pageIndex,
      pageSize,
      sortBy: sorting[0]?.id || '',
      sortDesc: sorting[0]?.desc ?? false
    }),
    [sorting]
  )

  // Update rowSelectionByPages when user interacts with checkboxes.
  // DataGrid fires onRowSelectionChange with the full merged selection (all pages),
  // so we extract only the current page's portion and store it.
  //
  // CRITICAL: In server-side mode (onChangeControl), DataGridTable internally calls
  // setRowSelection({}) on every pagination/sorting change (DataGridTable line ~910).
  // This forced clear propagates here as onRowSelectionChange({}). We detect it via
  // dgInternalClearRef (set in onChangeControl/sorting handlers) and skip + resync.
  const handleRowSelectionChange = useCallback(
    (newSelection: Record<string, boolean>) => {
      const currentAll = allRowSelectionRef.current

      // Efficiently grab active keys as Sets
      const newActiveKeys = getActiveKeys(newSelection)
      const curActiveKeys = getActiveKeys(currentAll)

      // If identical → spurious sync or re-render echo → skip
      if (areSetsEqual(newActiveKeys, curActiveKeys)) return

      // If DataGrid is clearing due to internal pagination/sort change → skip + resync
      if (dgInternalClearRef.current) {
        dgInternalClearRef.current = false
        setResyncTrigger((prev) => prev + 1)
        return
      }

      // Built-in "Clear Selection" button: empty incoming + we have selections + not internal clear.
      // Wipe selections across ALL pages (not just current).
      if (newActiveKeys.size === 0 && curActiveKeys.size > 0) {
        setRowSelectionByPages({})
        selectedItemsMapRef.current.clear()
        setIsAllDataSelected(false)
        return
      }

      // Genuine user interaction (check/uncheck). Extract current page's portion using the Set.
      const newPagePortion: Record<string, boolean> = {}
      tableData.entries.forEach((entry: any) => {
        const id = String(entry.id)
        if (newActiveKeys.has(id)) {
          newPagePortion[id] = true
        }
      })

      // Update selectedItemsMap: add newly selected items from current page entries
      tableData.entries.forEach((entry: any) => {
        const id = Number(entry.id)
        if (newPagePortion[String(id)]) {
          selectedItemsMapRef.current.set(id, { id, name: entry.name })
        } else {
          selectedItemsMapRef.current.delete(id)
        }
      })

      // Only update state if the page portion actually changed.
      // Without this guard, DataGrid re-emitting stale off-page selections after
      // a bulk clear would create new state references (even for empty page portions),
      // causing infinite re-render loops.
      setRowSelectionByPages((prev) => {
        const existingPageKeys = getActiveKeys(prev[pagination.pageIndex])
        const newPageKeys = getActiveKeys(newPagePortion)

        if (areSetsEqual(existingPageKeys, newPageKeys)) return prev

        // Clean up empty page entries when the user unchecked everything on this page
        if (newPageKeys.size === 0) {
          const nextState = { ...prev }
          delete nextState[pagination.pageIndex]
          return nextState
        }

        return { ...prev, [pagination.pageIndex]: newPagePortion }
      })
    },
    [pagination.pageIndex, tableData.entries]
  )

  // React to sorting changes - only AFTER initial filter-triggered load
  useEffect(() => {
    const prev = prevSortingRef.current
    prevSortingRef.current = sorting

    if (hasDataLoadedRef.current && sorting.length > 0 && sorting !== prev) {
      // Clear all selections when sorting changes
      setRowSelectionByPages({})
      selectedItemsMapRef.current.clear()
      setIsAllDataSelected(false)
      setTotalCount(null)
      // Update ref immediately so onChangeControl won't set dgInternalClearRef
      allRowSelectionRef.current = {}

      setPagination((prevPag) => {
        if (prevPag.pageIndex !== 0) {
          lastPaginationSourceRef.current = 'sort'
        }
        return { pageIndex: 0, pageSize: prevPag.pageSize }
      })
      tableData.load({
        pageStart: 0,
        pageSize: pagination.pageSize,
        sortBy: sorting[0].id,
        sortDesc: sorting[0].desc
      })
    }
  }, [sorting])

  // Fetch data when pagination changes (user-initiated only)
  useEffect(() => {
    if (lastPaginationSourceRef.current !== 'user') {
      lastPaginationSourceRef.current = 'user'
      return
    }
    if (hasDataLoadedRef.current) {
      tableData.load(buildLoadParams(pagination.pageIndex, pagination.pageSize))
    }
  }, [pagination.pageIndex, pagination.pageSize])

  // Fetch total count when first row is selected and no cached count exists
  useEffect(() => {
    if (selectedIds.length > 0 && totalCount === null && !isCountLoading) {
      loadCount()
    }
  }, [selectedIds.length, totalCount, isCountLoading, loadCount])

  const handleBulkSelectAll = useCallback(() => {
    // Select all selectable rows on the current page (mirrors library's handleSelectAllRows)
    // Uses same conditions as isRowSelectable: exclude folders, workflow items, placeholders, invalid
    const currentPageSelection: Record<string, boolean> = {}
    tableData.entries.forEach((entry: any) => {
      const isFolder = entry.type === FsiType.FOLDER
      const isInWorkflow = entry.workflow?.length > 0
      const isInvalid = !entry.valid
      if (!(isFolder || isInWorkflow || isInvalid)) {
        currentPageSelection[String(entry.id)] = true
      }
    })

    // Update selectedItemsMap with the newly bulk-selected items
    tableData.entries.forEach((entry: any) => {
      if (currentPageSelection[String(entry.id)]) {
        selectedItemsMapRef.current.set(entry.id, { id: entry.id, name: entry.name })
      }
    })

    setRowSelectionByPages((prev) => ({
      ...prev,
      [pagination.pageIndex]: currentPageSelection
    }))

    setIsAllDataSelected(true)
  }, [tableData.entries, pagination.pageIndex])

  const handleBulkClearSelection = useCallback(() => {
    // Update ref immediately so DataGrid's stale onRowSelectionChange callback
    // (which may fire with old selection before React commits our state update)
    // sees an empty ref and the identity check skips it.
    allRowSelectionRef.current = {} //c-r
    setRowSelectionByPages({})
    selectedItemsMapRef.current.clear()
    setIsAllDataSelected(false)
    setTotalCount(null) //c-r
  }, [])

  // Cursor pagination: manualDataLength is derived from hasNextPage so the DataGrid
  // believes there's at least one more page when the API signals more data exists.
  // DataGrid computes pageCount = manualDataLength / initialPageSize, and TanStack
  // Table's canNextPage check is `pageIndex < pageCount - 1`. To keep pageCount as
  // a clean integer (so the next-arrow enables/disables correctly), we add a full
  // pageSize slot when hasNextPage is true (instead of just +1, which produced a
  // fractional pageCount that could collapse to the same page on certain edge cases).
  // Formula: (pageIndex + 1) * pageSize + (hasNextPage ? pageSize : 0)
  const manualDataLength = useMemo(
    () => (pagination.pageIndex + 1) * pagination.pageSize + (tableData.hasNextPage ? pagination.pageSize : 0),
    [pagination.pageIndex, pagination.pageSize, tableData.hasNextPage]
  )

  // Loading state — count loading removed
  const isLoading = tableData.isLoading

  // Guard: DataGrid's internal useEffect compares tableRows.length with
  // Object.entries(rowSelection).length. In cross-page selection mode,
  // rowSelection (our allRowSelectionObj) has more entries than the current page,
  // so the useEffect immediately calls setSelectAll(false) after the user clicks
  // "Select All", causing onAllDataSelected to fire (true) then (false) in rapid
  // succession. We debounce by ignoring a false that follows a true within 100ms.
  const allDataSelectedAtRef = useRef<number>(0)
  const handleOnAllDataSelected = useCallback((isAllSelected: boolean) => {
    if (isAllSelected) {
      allDataSelectedAtRef.current = Date.now()
      setIsAllDataSelected(true)
    } else {
      // Ignore spurious false within 100ms of a true
      if (Date.now() - allDataSelectedAtRef.current < 100) {
        return
      }
      allDataSelectedAtRef.current = 0
      setIsAllDataSelected(false)
    }
  }, [])

  // Define columns
  const columns = useMemo(() => {
    // Name column - always shown
    const nameColumn = columnHelper.accessor('name', {
      header: translate('NAME'),
      id: 'name',
      size: 480,
      maxSize: 700,
      textAlign: 'left',
      enableSorting: isSortingEnabled,
      enableResizing: true,
      meta: { sortKey: 'NAME' },
      cell: ({ row }: any) => {
        const item = row.original
        const isFolder = item.type === FsiType.FOLDER
        const isPlaceholder = item.type === FsiType.PLACEHOLDER
        const isBrowseOnlyNonFolder = item.acl === AclLevel.BROWSE && !isFolder
        const isInvalid = !item.valid

        return (
          <div className="d-flex align-items-center" style={{ gap: '6px', minHeight: '28px', minWidth: 0 }}>
            {/* Valid indicator - only for non-folders */}
            {item.type !== FsiType.FOLDER && (
              <span
                style={{
                  display: 'inline-block',
                  flexShrink: 0
                }}
              >
                <ValidDocIndicator fsi={item} />
              </span>
            )}

            {/* Document icon - constrain size for DataGrid row */}
            <span
              style={{
                display: 'inline-flex',
                flexShrink: 0,
                width: '24px',
                height: '24px',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <DocIcon fsi={item} />
            </span>

            {/* Quality issue indicator */}
            {shouldShowQualityIssueIndicator(fsi, item) && <WrapperOverlay row={item} />}

            {/* Name with conditional rendering */}
            <div
              className="ellipsis"
              title={getFsiNameTooltip(item)}
              style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {isPlaceholder || isBrowseOnlyNonFolder || isInvalid ? (
                <span>
                  {item.name}
                  {isEtmfEnabled && isPlaceholder && (
                    <DueDateIndicator dueDateStatus={item.dueDateStatus} translate={translate} />
                  )}
                </span>
              ) : item.isReport ? (
                <TextLinkButton action={() => window.open(`/api/files/${item.id}/download`, '_blank')}>
                  {item.name}
                </TextLinkButton>
              ) : (
                <a
                  href={buildUrlWithCSelections(
                    isFolder ? `/browser/${item.id}?type=${item.systemFolderType}&page=1` : `/show-file/${item.id}`
                  )}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.button !== 0) return
                    e.preventDefault()
                    handleRowEnter(item)
                  }}
                >
                  {item.name}
                </a>
              )}
            </div>

            {/* Document state indicator */}
            <DocState fsi={item} />
          </div>
        )
      }
    })

    // Site Number column - conditional
    const siteNumberColumn = columnHelper.accessor('supplementalData.siteNumber', {
      header: translate('TABLE_TH_STUDY_ENV_SITE_NO'),
      id: 'siteNumber',
      size: 120,
      textAlign: 'left',
      enableResizing: false,
      cell: ({ row }: any) =>
        row.original.supplementalData?.siteNumber || row.original.suppMetadataValue || translate('NOT_AVAILABLE')
    })

    // Data Page column - conditional
    const dataPageColumn = columnHelper.accessor('supplementalData.dataPageName', {
      header: translate('TABLE_TH_DATAPAGE'),
      id: 'dataPage',
      size: 120,
      textAlign: 'left',
      enableResizing: false,
      cell: ({ row }: any) => row.original.supplementalData?.dataPageName || translate('NOT_AVAILABLE')
    })

    // Subject column - conditional
    const subjectColumn = columnHelper.accessor('supplementalData.subjectName', {
      header: translate('TABLE_TH_SUBJECT'),
      id: 'subject',
      size: 120,
      textAlign: 'left',
      enableResizing: false,
      cell: ({ row }: any) => row.original.supplementalData?.subjectName || translate('NOT_AVAILABLE')
    })

    // Version column - conditional (shown unless hideVersion is true)
    const versionColumn = columnHelper.accessor('fileVersionNo', {
      header: translate('VERSION'),
      id: 'version',
      size: 70,
      textAlign: 'left',
      enableSorting: isSortingEnabled,
      enableResizing: false,
      meta: { sortKey: 'VERSION' },
      cell: ({ row }: any) => (
        <span title={row.original.fileVersionNo || ''}>{row.original.fileVersionNo || translate('NOT_AVAILABLE')}</span>
      )
    })

    // QC Review Status column - conditional (property search + qcReviewEnabled)
    const qcStatusColumn = columnHelper.accessor('qcReviewStatus', {
      header: translate('QC_REVIEW_STATUS_FILTER'),
      id: 'qcReviewStatus',
      size: 150,
      textAlign: 'left',
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }: any) => row.original.qcReviewStatus || '-'
    })

    // Workflow column - always shown
    const workflowColumn = columnHelper.accessor('workflow', {
      header: translate('CHANGE_REQUEST'),
      id: 'workflow',
      size: 120,
      textAlign: 'left',
      enableSorting: isSortingEnabled,
      enableResizing: false,
      meta: { sortKey: 'WORKFLOW' },
      cell: ({ row }: any) => {
        const item = row.original
        if (!item.workflow) {
          return translate('NOT_AVAILABLE')
        }

        if (access.isAllowed('workflowsAccessDetail')) {
          return (
            <TextLinkButton
              action={() => {
                if (item.workflow.length === 1) {
                  showModal(<WorkflowDetailsModal wfId={item.workflow[0].id} onClose={() => {}} />)
                } else {
                  showModal(<MultipleWorkflowsModal workflow={item.workflow} onClose={() => {}} />, {
                    keepParentModalsOpen: true
                  })
                }
              }}
            >
              {item.workflow.length === 1 ? item.workflow[0].formattedId : translate('WORKFLOW_MULTIPLE')}
            </TextLinkButton>
          )
        }

        return item.workflow.length === 1 ? item.workflow[0].formattedId : translate('WORKFLOW_MULTIPLE')
      }
    })

    // Updated On column - always shown
    const updatedOnColumn = columnHelper.accessor('updatedOn', {
      header: translate('TABLE_TH_UPDATED_ON'),
      id: 'updatedOn',
      size: 162,
      textAlign: 'left',
      enableSorting: isSortingEnabled,
      enableResizing: false,
      meta: { sortKey: 'UPDATED_ON' },
      cell: ({ row }: any) => formatDateTime(row.original.updatedOn)
    })

    // Actions column - always shown
    const actionsColumn = columnHelper.display({
      id: 'actions',
      header: translate('ACTIONS'),
      size: 68,
      enableResizing: false,
      cell: ({ row }: any) => {
        const item = row.original
        if (!item.valid) return null

        return (
          <PropertySearchRowMenu
            row={row}
            selectedIds={selectedIds}
            selectedItems={selectedItems}
            deselectAll={deselectAll}
            parentFsi={fsi}
            isAllDataSelected={isAllDataSelected}
            totalCount={totalCount}
          />
        )
      }
    })

    // Build columns array with conditional columns
    return [
      nameColumn,
      ...(isSiteNumberShown ? [siteNumberColumn] : []),
      ...(isDataPageShown ? [dataPageColumn] : []),
      ...(isSubjectShown ? [subjectColumn] : []),
      ...(!hideVersion ? [versionColumn] : []),
      ...(qcReviewEnabled ? [qcStatusColumn] : []),
      workflowColumn,
      updatedOnColumn,
      actionsColumn
    ]
  }, [
    translate,
    isSortingEnabled,
    isSiteNumberShown,
    isDataPageShown,
    isSubjectShown,
    hideVersion,
    qcReviewEnabled,
    fsi,
    selectedIds,
    deselectAll,
    shouldShowQualityIssueIndicator,
    getFsiNameTooltip,
    isEtmfEnabled,
    handleRowEnter,
    access,
    showModal,
    formatDateTime
  ])

  return (
    <div className="property-search-datagrid">
      <PropertySearchBulkActionHeader
        selectedCount={selectedIds.length}
        isAllDataSelected={isAllDataSelected}
        totalCount={totalCount}
        isCountLoading={isCountLoading}
        onSelectAll={handleBulkSelectAll}
        onClearSelection={handleBulkClearSelection}
      />
      <DataGrid
        enableColumnResizing={true}
        dataTestId="property-search-datagrid"
        columns={columns}
        data={tableData.entries}
        getRowId={useCallback((row: any) => String(row.id), [])}
        isLoading={isLoading}
        // pageCount removed — using cursor pagination
        enablePagination={true}
        pagination={pagination}
        enableGlobalSearch={false}
        dataGridControlProps={{
          hasActionBtn: false,
          hasViewOptions: false
        }}
        manualDataLength={manualDataLength}
        extraPagination={{ isCursorPagination: true }}
        enableSorting={isSortingEnabled}
        enableRowSelection={true}
        options={{ enableRowSelection: isRowSelectable }}
        enableColumnsConfiguration={true}
        lockedColumnsIds={['name', 'actions']}
        onPaginationChange={setPagination}
        onChangeControl={useCallback((state: any) => {
          if (state.pagination) {
            // Mark that the next onRowSelectionChange({}) is from DataGrid's internal
            // clear (not a user action), so handleRowSelectionChange can skip it.
            // Only set when there are actual selections — otherwise the flag stays stale
            // because DataGrid's setRowSelection({}) is a no-op when already empty.
            if (Object.keys(allRowSelectionRef.current).length > 0) {
              dgInternalClearRef.current = true
            }
            setPagination(state.pagination)
            // Persist pageSize so RcmTableDataWrapper.refreshTable() uses the same value
            // on subsequent filter applications
            persistService.setGlobal('paginate', {
              page: state.pagination.pageIndex + 1,
              perPage: state.pagination.pageSize
            })
          }
        }, [])}
        onAllDataSelected={handleOnAllDataSelected}
        onSortingChange={handleSortingChange}
        onRowSelectionChange={handleRowSelectionChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        state={{
          pagination,
          sorting,
          rowSelection: allRowSelectionObj,
          columnOrder,
          columnPinning,
          columnVisibility
        }}
        rowsPerPageOptions={PAGE_SIZES_ARRAY}
        noResultsText={translate('NO_RESULTS_FILTERED')}
      />
    </div>
  )
}

export default PropertySearchDataGrid
